import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearPortalLocalStorage } from '@rsos/base-utils/clearPortalLocalStorage';
import { PORTAL_SESSION_PRIORITY } from '@rsos/capstone/src/constants/portalPriorities';
import { gspx } from '@rsos/gainsight';
import {
  logout,
  sessionEnd,
  sessionStart as startSession,
} from '@rsos/sinatra';
import { getSessionNotifyAge } from '@rsos/utils/metaTags';
import SessionModal from '../SessionModal';

const SESSION_EXPIRED_REDIRECT_PATH = '/?session_expired=true';
const SECOND_WARNING_LEAD_SECONDS = 300;
const MIN_SECONDS_AFTER_SESSION_START_FOR_WARNING = 1;
const DEFAULT_SESSION_NOTIFY_AGE_SECONDS = 600;

// Keep the existing active-user final warning, but treat a timer that resumes
// more than this far past expiration as an already-expired session and log out
// directly instead of flashing the tiny "expiring" modal on return.
const FINAL_WARNING_GRACE_PERIOD_SECONDS = 3;

const getPositiveFiniteSeconds = (value, fallback) => {
  return Number.isFinite(value) && value > 0 ? value : fallback;
};

const getSessionKey = (sessionStart, sessionExpiration) =>
  `${sessionStart}:${sessionExpiration}`;

const hasUsableSessionClock = (sessionStart, sessionExpiration) => {
  return (
    Number.isFinite(sessionStart) &&
    Number.isFinite(sessionExpiration) &&
    sessionExpiration > sessionStart
  );
};

const getWarningSchedule = (sessionStart, sessionExpiration) => {
  const sessionNotifyAge = getPositiveFiniteSeconds(
    getSessionNotifyAge(),
    DEFAULT_SESSION_NOTIFY_AGE_SECONDS,
  );

  // Pre-expiry warnings are lead-time based: "show this warning N seconds
  // before expiration." That works for normal 48-hour sessions but becomes
  // unsafe when a stack is accidentally configured with a short SESSION_AGE:
  // expiration - 300 can be equal to, or earlier than, sessionStart. Showing a
  // warning at login is the failure mode that made users think logout/login
  // could not clear the timeout.
  //
  // Keep the historic warning order:
  //   first  = the earlier/larger lead time, normally 10 minutes
  //   second = the later/smaller lead time, normally 5 minutes
  //
  // Then drop any warning whose computed time is outside this exact session's
  // usable window. We intentionally do not clamp a warning to sessionStart,
  // because clamping a 10-minute warning for a 5-minute session would recreate
  // the immediate-login warning under a different timestamp.
  const warningLeads = [
    {
      warning: 'first',
      lead: Math.max(sessionNotifyAge, SECOND_WARNING_LEAD_SECONDS),
    },
    {
      warning: 'second',
      lead: Math.min(sessionNotifyAge, SECOND_WARNING_LEAD_SECONDS),
    },
  ];

  const earliestWarningTime =
    sessionStart + MIN_SECONDS_AFTER_SESSION_START_FOR_WARNING;
  const latestWarningTime = sessionExpiration - 1;
  return warningLeads.reduce((schedule, { warning, lead }) => {
    const warningTime = sessionExpiration - lead;

    if (warningTime < earliestWarningTime || warningTime > latestWarningTime) {
      return schedule;
    }

    const duplicateWarningIndex = schedule.findIndex(
      scheduledWarning => scheduledWarning.warningTime === warningTime,
    );

    if (duplicateWarningIndex >= 0) {
      // SESSION_NOTIFY_AGE can be exactly 300, which makes the historical
      // first and second thresholds identical. The old if/else chain checked
      // "second" before "first", so keep that priority instead of showing a
      // lower-priority duplicate warning at the same timestamp.
      schedule[duplicateWarningIndex] = { warning, warningTime };
      return schedule;
    }

    schedule.push({ warning, warningTime });
    return schedule;
  }, []);
};

/**
 * Orchestrates the three-stage session timeout warning sequence:
 *   1. "first"  - SESSION_NOTIFY_AGE seconds before expiry
 *                  default: 600s / 10 minutes
 *   2. "second" - 300s / 5 minutes before expiry
 *   3. "final"  - at expiry; SessionModal auto-logs out after 3s
 *
 * This is a fixed wall-clock countdown from login, not an inactivity timer:
 * moving the mouse, clicking, typing, or making API calls does not extend it.
 *
 * Numeric defaults:
 *   SESSION_AGE        = 172800s / 48 hours
 *   SESSION_NOTIFY_AGE = 600s / 10 minutes
 *   second warning     = 300s / 5 minutes
 *
 * SESSION_AGE and SESSION_NOTIFY_AGE come from portal_renderer HTML meta tags.
 * The shared fallbacks above apply when those meta tags are absent.
 */
const SessionPortal = () => {
  const [isActive, setIsActive] = useState(false);
  const [warning, setWarning] = useState(null);
  const dispatch = useDispatch();

  // sessionStart/sessionExpiration are Unix timestamps in seconds. sessionStart()
  // writes them as now and now + SESSION_AGE; with the default fallback that is
  // now + 172800 seconds (48 hours). They are persisted to localStorage through
  // redux-persist, so page reloads do not reset or extend the session clock.
  const session = useSelector(state => state.sinatra.auth.session) || {};

  // Tracks which warnings have fired for the current session clock.
  // Using a ref avoids re-renders on every tick. Resets when sessionStart or
  // sessionExpiration changes so a new login replays the full warning sequence.
  const shownWarningsRef = useRef({
    sessionKey: null,
    warnings: new Set(),
  });
  const isLoggingOutRef = useRef(false);

  const handleClose = useCallback(() => {
    setIsActive(false);
  }, []);

  const handleLogout = useCallback(() => {
    if (isLoggingOutRef.current) {
      return;
    }

    isLoggingOutRef.current = true;
    handleClose();
    clearPortalLocalStorage();
    dispatch(sessionEnd());
    gspx.trackCustomEvent('Auth Session', {
      name: 'Session End',
      Category: 'Session tracking',
      'Launched date': new Date(),
    });
    dispatch(logout());
    // Reuse the auth app's existing session-expired path so the user lands on
    // sign-in with the specific expired-session message instead of a generic
    // API-error toast from the app that is being unloaded.
    window.location.assign(SESSION_EXPIRED_REDIRECT_PATH);
  }, [dispatch, handleClose]);

  useEffect(() => {
    const { sessionStart, sessionExpiration } = session;

    // PersistGate normally waits for redux-persist before LayoutContent mounts.
    // If the values are still missing here, treat it as an authenticated
    // browser session that never initialized the portal clock.
    //
    // Backward compatibility: older SSO sessions may already have valid tokens
    // but no sessionStart/sessionExpiration because the SSO login screens used
    // to skip sessionStart(). Treat non-numeric persisted values the same way:
    // they cannot drive a reliable countdown and would otherwise disable the
    // timeout silently. SessionPortal is only mounted for logged-in users, so
    // initialize a fresh 172800s / 48-hour default clock instead of forcing a
    // surprise logout. New SSO logins now set this explicitly; this branch
    // covers existing persisted sessions and any future login path that forgets
    // to start the clock.
    //
    // Also treat expiration <= start as invalid. That catches corrupted
    // persisted state and malformed non-positive SESSION_AGE values without
    // letting the app immediately enter the final warning loop.
    if (!hasUsableSessionClock(sessionStart, sessionExpiration)) {
      dispatch(startSession());
      return undefined;
    }

    // Reset warning state for the new session expiration value.
    const sessionKey = getSessionKey(sessionStart, sessionExpiration);
    shownWarningsRef.current = {
      sessionKey,
      warnings: new Set(),
    };
    setIsActive(false);
    setWarning(null);

    // Check immediately on mount. If redux-persist restored an already-expired
    // portal clock, do not open the final warning modal. The user is not
    // watching a live countdown anymore; they are returning to an expired
    // browser session, so clear local auth state and redirect immediately.
    checkIsSessionExpiring(sessionStart, sessionExpiration, {
      logoutExpiredSessionImmediately: true,
    });

    // sessionStart/sessionExpiration are passed as arguments so the interval
    // always uses the values captured when this effect ran, avoiding stale
    // closure issues.
    const interval = setInterval(() => {
      checkIsSessionExpiring(sessionStart, sessionExpiration);
    }, 1000);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      // Returning to a backgrounded tab after the wall-clock session expired is
      // the same UX case as mounting with expired persisted state: skip the tiny
      // "expiring" modal and finish logout directly.
      checkIsSessionExpiring(sessionStart, sessionExpiration, {
        logoutExpiredSessionImmediately: true,
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Tear down interval/listener when the session clock changes or unmounts.
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // checkIsSessionExpiring and session are intentionally omitted: the function
    // is defined in the same scope and session values are passed as arguments to
    // avoid stale closures — the deps array drives re-runs, not the function ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, handleLogout, session.sessionExpiration, session.sessionStart]);

  /**
   * Determines which warning stage to show based on current time vs expiration.
   *
   * Thresholds:
   *   firstWarning  = sessionExpiration - max(SESSION_NOTIFY_AGE, 300)
   *                 = sessionExpiration - 600 by default
   *                 = 10 minutes before default expiry
   *   secondWarning = sessionExpiration - min(SESSION_NOTIFY_AGE, 300)
   *                 = sessionExpiration - 300 by default
   *                 = 5 minutes before default expiry
   * Any pre-expiry warning whose computed time is not strictly after
   * sessionStart and strictly before sessionExpiration is skipped. This is
   * deliberate: short or misconfigured sessions should not show a timeout
   * warning immediately after login.
   *
   * max/min normalization ensures 'first' always fires before 'second' even if
   * SESSION_NOTIFY_AGE is configured below 300s. For example, a configured
   * SESSION_NOTIFY_AGE of 120s still produces a first warning at 300s and a
   * second warning at 120s, preserving the warning order.
   *
   * >= instead of === ensures warnings fire even if the interval was delayed
   * by browser throttling (backgrounded tab, sleep, etc).
   */
  function checkIsSessionExpiring(
    sessionStart,
    sessionExpiration,
    { logoutExpiredSessionImmediately = false } = {},
  ) {
    if (
      isLoggingOutRef.current ||
      !hasUsableSessionClock(sessionStart, sessionExpiration)
    ) {
      return;
    }

    // Sync ref if the session clock changed between effect runs. Include both
    // start and expiration in the key so a freshly started session with the same
    // absolute expiration value still gets a clean warning history.
    const sessionKey = getSessionKey(sessionStart, sessionExpiration);
    if (shownWarningsRef.current.sessionKey !== sessionKey) {
      shownWarningsRef.current = {
        sessionKey,
        warnings: new Set(),
      };
    }

    const dateNowSeconds = Math.floor(Date.now() / 1000);

    if (dateNowSeconds >= sessionExpiration) {
      const finalWarningWasMissed =
        dateNowSeconds - sessionExpiration >=
        FINAL_WARNING_GRACE_PERIOD_SECONDS;

      if (logoutExpiredSessionImmediately || finalWarningWasMissed) {
        handleLogout();
        return;
      }

      // Expired while this tab was still checking on time. Keep the existing
      // short final grace path for active users; SessionModal will call
      // handleLogout after three seconds.
      showWarning('final');
      return;
    }

    const dueWarnings = getWarningSchedule(
      sessionStart,
      sessionExpiration,
    ).filter(({ warningTime }) => dateNowSeconds >= warningTime);

    if (dueWarnings.length) {
      // If browser throttling, sleep, or tab suspension skips over more than
      // one warning threshold, show the latest due warning. This preserves the
      // old final > second > first priority without relying on hardcoded if/else
      // branches that can drift from the schedule calculation above.
      showWarning(dueWarnings[dueWarnings.length - 1].warning);
    }
  }

  // Each warning fires exactly once per sessionExpiration value.
  // shownWarningsRef prevents re-opening the modal on every tick after a threshold is crossed.
  function showWarning(nextWarning) {
    if (
      isLoggingOutRef.current ||
      shownWarningsRef.current.warnings.has(nextWarning)
    ) {
      return;
    }

    shownWarningsRef.current.warnings.add(nextWarning);
    setIsActive(true);
    setWarning(nextWarning);
  }

  return (
    <SessionModal
      isOpened={isActive}
      onLogout={handleLogout}
      onClose={handleClose}
      expiresAt={session.sessionExpiration}
      warning={warning}
      portalPriority={PORTAL_SESSION_PRIORITY}
    />
  );
};

export default SessionPortal;
